import { Button, Upload, UploadProps } from "antd";
import { UploadChangeParam, UploadFile } from "antd/es/upload";
import { useEffect, useState } from "react";
import { useUploadFileMutation } from "../../features/portal/fileUpload/fileUpload";
import useError from "../../hooks/useError";
import { stringToArray } from "../../utils/array";

type Props = {
  errorMessage?: string;
  form: any;
  name: string;
  disabled?: boolean;
  fileType?: boolean;
  accept?: string;
  onChange?: (url: string | null) => void;
  listFile?: boolean;
  size?: "large" | "middle" | "small";
  fileSize?: number; // MB
} & Omit<UploadProps, "name">;

export default function EmployeeFileUpload({
  errorMessage = "File upload fail",
  form,
  name,
  disabled,
  onChange,
  accept = ".png,.jpeg",
  listFile = true,
  fileType = true,
  size = "large",
  fileSize,
}: Props) {
  const [fileName, setFileName] = useState("No file chosen");
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploadFile, { isLoading, isError, error, data }] =
    useUploadFileMutation();
  useError(isError, error, errorMessage);

  const handleUpload = (file: File) => {
    if (fileSize && file.size / 1024 / 1024 > fileSize) {
      form.setFields([
        {
          name,
          errors: [`File size exceeds the ${fileSize}MB limit`],
        },
      ]);
      return Upload.LIST_IGNORE;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("name", file.name);

    uploadFile(formData);
    return false;
  };

  const handleChange = (info: UploadChangeParam<UploadFile<any>>) => {
    setFileList(info.fileList);

    if (info.file.status === "removed") {
      onChange && onChange(null);
      setFileName("No file chosen");
    }
  };

  useEffect(() => {
    if (data?.Location) {
      setFileName(data?.Location);
      onChange && onChange(data?.Location);
    }
  }, [data?.Location, onChange]);

  useEffect(() => {
    if (fileType) {
      const timer = setTimeout(() => {
        const file = form.getFieldValue(
          true ? stringToArray(name, ".") : [name]
        );

        if (file) {
          onChange && onChange(file);
          setFileName(file);
        }
      }, 1000);

      const file = form.getFieldValue(
        listFile ? stringToArray(name, ".") : [name]
      );

      if (file) {
        onChange && onChange(file);
        setFileName(file);
      }

      return () => {
        clearTimeout(timer);
      };
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, name, fileType, listFile]);

  return (
    <Upload
      name="file"
      beforeUpload={handleUpload}
      maxCount={1}
      accept={accept}
      showUploadList={false}
      onChange={handleChange}
      fileList={fileList}
      disabled={disabled || isLoading}
    >
      <Button
        className={`flex items-center !justify-start !p-0 !border-display-medium rounded-md ${
          size === "large" ? "!h-[46px]" : ""
        }`}
        block
        size={size}
        disabled={disabled}
        htmlType="button"
      >
        <span className="!flex justify-center items-center px-3 bg-[#5e718d] text-white h-full rounded-s">
          Browse file
        </span>
        <div className="px-2.5 gap-2 text-sm font-medium text-extra-gray overflow-hidden whitespace-nowrap text-ellipsis">
          {isLoading ? (
            "Uploading..."
          ) : (
            <span
              className="overflow-hidden text-ellipsis"
              style={{
                direction: "unset",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {fileName?.split("/")?.pop() || "No file chosen"}
            </span>
          )}
        </div>
      </Button>
    </Upload>
  );
}
